import { Component } from '@angular/core';
import { LoanStatusService } from 'src/app/services/sales/loan-status.service';
import { FormField } from 'src/app/interfaces/form-field.model';
import { NotifierService } from 'angular-notifier';
import { faSave } from "@fortawesome/free-solid-svg-icons";

@Component({
  selector: 'app-loan-status',
  templateUrl: './loan-status.component.html',
  styleUrls: ['./loan-status.component.css'],
})

export class LoanStatusComponent {

  // Icons imports
  faSave = faSave;

  formFields: FormField[] = [
    { type: 'search', id: 'buyersOrderNumber', label: "Buyer's Order Number", searchEvent: true, searchType: 'buyersOrder' },
    { type: 'search', id: 'sfdcNumber', label: 'SFDC Number', searchEvent: true, searchType: 'sfdc' },
    { type: 'text', id: 'nricNumber', label: 'NRIC No/RCB No/Passport No' },
    { type: 'text', id: 'nameAsInNRIC', label: 'Name as in NRIC/Passport' },
    { type: 'number', id: 'loanAmount', label: 'Loan Amount ($)', required: true },
    { type: 'number', id: 'loanPeriod', label: 'Loan Period (Months)', required: true },
    { type: 'number', id: 'loanRate', label: 'Loan Rate (%)', required: true },
    { type: 'number', id: 'monthlyInstallment', label: 'Monthly Installment ($)', lock: true },
    { type: 'text', id: 'financeCompany1', label: 'Finance Company 1', lock: true },
    {
      type: 'select',
      id: 'financeCompanyStatus1',
      label: 'Status',
      options: [
        { value: 'P', display: 'Pending' },
        { value: 'A', display: 'Approved' },
        { value: 'R', display: 'Rejected' },
      ],
      lock: true 
    },
    { type: 'text', id: 'financeCompany2', label: 'Finance Company 2', lock: true },
    {
      type: 'select',
      id: 'financeCompanyStatus2',
      label: 'Status',
      options: [
        { value: 'P', display: 'Pending' },
        { value: 'A', display: 'Approved' },
        { value: 'R', display: 'Rejected' },
      ],
      lock: true 
    },
    { type: 'text', id: 'financeCompany3', label: 'Finance Company 3', lock: true },
    {
      type: 'select',
      id: 'financeCompanyStatus3',
      label: 'Status',
      options: [
        { value: 'P', display: 'Pending' },
        { value: 'A', display: 'Approved' },
        { value: 'R', display: 'Rejected' },
      ],
      lock: true 
    },
    { type: 'text', id: 'financeCompany4', label: 'Finance Company 4', lock: true },
    {
      type: 'select',
      id: 'financeCompanyStatus4',
      label: 'Status',
      options: [
        { value: 'P', display: 'Pending' },
        { value: 'A', display: 'Approved' },
        { value: 'R', display: 'Rejected' },
      ],
      lock: true 
    },
    { type: 'switch', id: 'financeProblem', label: 'Finance Problem' },
    { type: 'date', id: 'loanSubmittedDate', label: 'Loan Submitted Date (dd/mm/yyyy)', required: true },
    { type: 'date', id: 'loanStatusDate', label: 'Loan Status Date (dd/mm/yyyy)', required: true },
    { type: 'text', id: 'remark', label: 'Remark' },
    { type: 'text', id: 'loanStatus', label: 'Loan Status', lock: true },
  ];

  get mainFormFields(): FormField[] {
    return this.formFields.slice(0, this.lowerFieldsStartIndex);
  }

  get lowerFormFields(): FormField[] {
    return this.formFields.slice(this.lowerFieldsStartIndex);
  }

  private get lowerFieldsStartIndex(): number {
    const index = this.formFields.findIndex(field => field.id === 'financeProblem');
    return index === -1 ? this.formFields.length : index;
  }

  constructor(
    private loanStatusService: LoanStatusService,
    private notifier: NotifierService
  ) {}

  fetchLoanStatus(event: any): void {
    const searchField = this.formFields.find(field => field.id === event.fieldId);
  
    if (!searchField || !searchField.value) {
      this.notifier.notify('error', 'Please enter a valid reference ID.');
      return;
    }
    
    const referenceId = String(searchField.value).replace(/['"]/g, '');
    
    const isSearchByRefNo = event.fieldId === 'buyersOrderNumber';
    
    this.loanStatusService.getLoanStatus(referenceId, isSearchByRefNo).subscribe({
      next: (response) => {
        //kconsole.log('Full response for GetLoanStatus:', response); 
    
        let data = null;

        if (response.code !== undefined && response.code !== 0) {
          this.notifier.notify(response.code > 0 ? 'warning' : 'error', response.message || 'No loan status details found.');
          return;
        }

        data = response.body;
    
        if (data) {
          this.updateFormFields(data);
        } else {
          this.notifier.notify('warning', 'No loan status details found.');
        }
      },
      error: (error) => {
        console.error('Error fetching loan status:', error);
        this.notifier.notify('error', 'Failed to fetch loan status.');
      },
    });    
  }
  
  private updateFormFields(data: any): void {
    const convertDateToYYYYMMDD = (dateString: string | null): string => {
      if (!dateString) return '';
      const [day, month, year] = dateString.split('/');
      return `${year}-${month}-${day}`;
    };
  
    this.setFieldValue('nricNumber', data.NrIc);              // NRIC No/RCB No/Passport No
    this.setFieldValue('nameAsInNRIC', data.Name);            // Name as in NRIC/Passport
    this.setFieldValue('loanAmount', data.LoanAmount);        // Loan Amount ($)
    this.setFieldValue('loanPeriod', data.LoanPeriod);        // Loan Period (Months)
    this.setFieldValue('loanRate', data.LoanRate);            // Loan Rate (%)
    this.setFieldValue('monthlyInstallment', data.MonthlyInstallment); // Monthly Installment ($)
  
    // Finance Company Names
    this.setFieldValue('financeCompany1', data.FinanceCompanyName1);
    this.setFieldValue('financeCompany2', data.FinanceCompanyName2);
    this.setFieldValue('financeCompany3', data.FinanceCompanyName3);
    this.setFieldValue('financeCompany4', data.FinanceCompanyName4);
  
    this.setFieldValue('financeCompanyStatus1', data.FinanceCompany1 || 'P');
    this.setFieldValue('financeCompanyStatus2', data.FinanceCompany2 || 'P');
    this.setFieldValue('financeCompanyStatus3', data.FinanceCompany3 || 'P');
    this.setFieldValue('financeCompanyStatus4', data.FinanceCompany4 || 'P');
  
    this.setFieldValue('financeProblem', !!data.FinanceStatus);
  
    this.setFieldValue('loanSubmittedDate', convertDateToYYYYMMDD(data.ApplicationDate));
    this.setFieldValue('loanStatusDate', convertDateToYYYYMMDD(data.LoanStatusDate));
  
    this.setFieldValue('remark', data.Remark);
    this.setFieldValue('loanStatus', data.LoanStatus);
  
    this.updateFieldLock('financeCompanyStatus1', data.DisableFinanceCompany1);
    this.updateFieldLock('financeCompanyStatus2', data.DisableFinanceCompany2);
    this.updateFieldLock('financeCompanyStatus3', data.DisableFinanceCompany3);
    this.updateFieldLock('financeCompanyStatus4', data.DisableFinanceCompany4);
  }

  private setFieldValue(fieldId: string, value: any): void {
    const field = this.formFields.find(f => f.id === fieldId);
    if (field) {
      field.value = value;
    } else {
      console.warn(`Field ${fieldId} not found.`);
    }
  }
  
  private updateFieldLock(fieldId: string, isLocked: boolean): void {
    const field = this.formFields.find(f => f.id === fieldId);
    if (field) {
      field.lock = isLocked; 
    } else {
      console.warn(`Field ${fieldId} not found.`);
    }
  }
  
  onFieldChange(event: any): void {
    if (event?.fields && Array.isArray(event.fields)) {
      event.fields.forEach((f: { id: string, value: any }) => {
      });
  
      const field = event.fields[event.updatedValue];
      if (field?.id === 'loanAmount') {
        this.updateMonthlyInstallment('amount');
      } else if (field?.id === 'loanPeriod') {
        this.updateMonthlyInstallment('period');
      } else if (field?.id === 'loanRate') {
        this.updateMonthlyInstallment('rate');
      }
    } else {
      console.warn('No detected fields');
    }
  }
  
  private updateMonthlyInstallment(source: 'amount' | 'period' | 'rate'): void {
    
    const buyersOrderNumber = String(this.formFields.find(f => f.id === 'buyersOrderNumber')?.value || '');
    const loanAmount = this.toServiceString(this.formFields.find(f => f.id === 'loanAmount')?.value);
    const loanPeriod = this.toServiceString(this.formFields.find(f => f.id === 'loanPeriod')?.value);
    const loanRate = this.toServiceString(this.formFields.find(f => f.id === 'loanRate')?.value);
  
    const payloadAmount = {
      LoanAmount: loanAmount,
      BuyersOrderNumber: buyersOrderNumber,
      LoanPeriod: loanPeriod,
      LoanRate: loanRate
    };

    const payloadBase = {
      LoanAmount: loanAmount,
      LoanPeriod: loanPeriod,
      LoanRate: loanRate
    };

    const request =
      source === 'amount'
        ? this.loanStatusService.changeLoanAmount(payloadAmount)
        : source === 'period'
          ? this.loanStatusService.changeLoanPeriod(payloadBase)
          : this.loanStatusService.changeLoanRate(payloadBase);
  
    request.subscribe({
      next: (response) => {
        if (response.code !== undefined && response.code !== 0) {
          this.notifier.notify('error', response.message || 'Unable to calculate monthly installment.');
          if (source === 'amount') {
            this.setFieldValue('loanAmount', null);
          } else if (source === 'period') {
            this.setFieldValue('loanPeriod', null);
          } else {
            this.setFieldValue('loanRate', null);
          }
          return;
        }
       
        const monthlyInstallment = response?.body?.MonthlyInstallment;
        const updatedValue = source === 'amount' ? response?.body?.LoanAmount : source === 'period' ? response?.body?.LoanPeriod : response?.body?.LoanRate;
  
        if (monthlyInstallment !== undefined) {
          if (updatedValue !== undefined) {
            this.setFieldValue(source === 'amount' ? 'loanAmount' : source === 'period' ? 'loanPeriod' : 'loanRate', updatedValue);
          }
          const installmentField = this.formFields.find(f => f.id === 'monthlyInstallment');
          if (installmentField) {
            installmentField.value = monthlyInstallment;
          } else {
            console.warn('Field "monthlyInstallment" not found on formFields.');
          }
        } else {
          console.warn('MonthlyInstallment value not found on API\'s response.');
        }
      },
      error: (error) => {
        console.error('changeLoanAmount API error:', error);
      }
    });
  }

  updateEntry(): void {
    const getFieldValue = (fieldId: string) => this.formFields.find(f => f.id === fieldId)?.value ?? null;
    const getLockStatus = (fieldId: string) => this.formFields.find(f => f.id === fieldId)?.lock ?? true;
  
    const formatDate = (date: string): string => {
      if (!date) return '';
      const [year, month, day] = date.split('-'); 
      return `${day}/${month}/${year}`;
    };

    const applicationDate = getFieldValue('loanSubmittedDate');
    const loanStatusDate = getFieldValue('loanStatusDate');

    if (!applicationDate || !loanStatusDate) {
      this.notifier.notify('error', 'Loan Submitted Date and Loan Status Date are required.');
      return;
    }

    if (!getFieldValue('buyersOrderNumber')) {
      this.notifier.notify('error', "Buyer's Order Number is required to update loan status.");
      return;
    }

    if (new Date(loanStatusDate) < new Date(applicationDate)) {
      this.notifier.notify('error', 'Loan Status Date must not be earlier than Loan Submitted Date.');
      return;
    }
  
    const payload = {
      model: {
        __type: "KAPS.Model.Sales.CrmLoanStatus",
        RefNo: getFieldValue('buyersOrderNumber'),
        NrIc: getFieldValue('nricNumber'),
        Name: getFieldValue('nameAsInNRIC'),
        LoanAmount: this.toNumberOrNull(getFieldValue('loanAmount')),
        LoanPeriod: this.toNumberOrNull(getFieldValue('loanPeriod')),
        LoanRate: this.toNumberOrNull(getFieldValue('loanRate')),
        MonthlyInstallment: this.toNumberOrNull(getFieldValue('monthlyInstallment')),
        FinanceCompany1: getFieldValue('financeCompanyStatus1') || null,
        FinanceCompany2: getFieldValue('financeCompanyStatus2') || null,
        FinanceCompany3: getFieldValue('financeCompanyStatus3') || null,
        FinanceCompany4: getFieldValue('financeCompanyStatus4') || null,
        FinanceCompanyName1: getFieldValue('financeCompany1'),
        FinanceCompanyName2: getFieldValue('financeCompany2'),
        FinanceCompanyName3: getFieldValue('financeCompany3'),
        FinanceCompanyName4: getFieldValue('financeCompany4'),
        DisableFinanceCompany1: getLockStatus('financeCompanyStatus1'),
        DisableFinanceCompany2: getLockStatus('financeCompanyStatus2'),
        DisableFinanceCompany3: getLockStatus('financeCompanyStatus3'),
        DisableFinanceCompany4: getLockStatus('financeCompanyStatus4'),
        FinanceStatus: !!getFieldValue('financeProblem'),
        ApplicationDate: formatDate(applicationDate),
        LoanStatusDate: formatDate(loanStatusDate),
        Remark: getFieldValue('remark'),
        LoanStatus: getFieldValue('loanStatus'),
      }
    };
  
    this.loanStatusService.updateLoanStatus(payload).subscribe({
      next: (response: any) => {
        if (response.statusCode === 200 && (response.code === undefined || response.code === 0)) {
          this.notifier.notify('success', response.message || 'Loan status updated successfully!');
          this.fetchLoanStatus({ fieldId: 'buyersOrderNumber' });
          return;
        }
        this.notifier.notify('error', response.message || 'Failed to update loan status.');
      },
      error: (error: any) => {
        console.error('API error:', error);
        this.notifier.notify('error', 'Failed to update loan status.');
      }
    });
  }

  private toServiceString(value: any): string {
    return value === null || value === undefined ? '' : String(value);
  }

  private toNumberOrNull(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
  }

  canUpdate(): boolean {
    const requiredFieldsComplete = this.formFields
      .filter(field => field.required)
      .every(field => this.isFieldFilled(field.value));
    const buyersOrderNumber = this.formFields.find(field => field.id === 'buyersOrderNumber')?.value;
    const applicationDate = this.formFields.find(field => field.id === 'loanSubmittedDate')?.value;
    const loanStatusDate = this.formFields.find(field => field.id === 'loanStatusDate')?.value;

    if (!requiredFieldsComplete || !this.isFieldFilled(buyersOrderNumber)) {
      return false;
    }

    if (applicationDate && loanStatusDate && new Date(loanStatusDate) < new Date(applicationDate)) {
      return false;
    }

    return true;
  }

  private isFieldFilled(value: any): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
  }
  
}
